// CTSK-509 / CTSK-514 — one note: markdown, colour-tinted, fit-to-content by
// default. The author can drag the bottom edge to save a height (the body then
// scrolls inside the card) and press "Fit to content" to go back. A link icon
// top-right copies the permalink. Edit/delete/resize show for the author only —
// that is UX; the API enforces author-only with a 403.

import { useEffect, useLayoutEffect, useRef, useState, type HTMLAttributes, type KeyboardEvent, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent } from 'react';
import { Check, Copy, GripVertical, Link2, Maximize2, Pencil, Trash2 } from 'lucide-react';
import type { Note, NoteColor } from '@captain-tasko/shared';
import { ConfirmModal } from '../ConfirmModal.js';
import { NoteEditor } from './NoteEditor.js';
import { NoteMarkdown } from './NoteMarkdown.js';
import { authorInitials, relativeTime } from './note-format.js';
import { clampHeight } from './layout.js';
import { copyText, notePermalink } from './copy-text.js';
import './notes.css';

export interface NoteCardProps {
  note: Note;
  /** Id of the signed-in user; null while unknown (no author controls then). */
  currentUserId: string | null;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: (id: string, patch: { body: string; color: NoteColor }) => void;
  onDelete: (id: string) => void;
  /** Used for the "Attached to" link. */
  onNavigate?: (path: string) => void;
  /** Save a height (px) or null = fit to content. Omit to hide resizing. */
  onResize?: (id: string, heightPx: number | null) => void;
  /** Grid view ignores the saved height (equal-height rows). */
  ignoreSavedHeight?: boolean;
  /** Drag grip props from dnd-kit; omit when the card cannot be reordered. */
  dragHandle?: HTMLAttributes<HTMLButtonElement> | null;
  isDragging?: boolean;
  /** Permalink target: scrolled to and outlined. */
  highlighted?: boolean;
}

const KEY_STEP = 24;

function targetPath(note: Note): string | null {
  if (!note.targetType || !note.targetId) return null;
  return note.targetType === 'document' ? `/documents/${note.targetId}` : `/workitems/${note.targetId}`;
}

export function NoteCard({
  note, currentUserId, canEdit, canDelete, onUpdate, onDelete, onNavigate,
  onResize, ignoreSavedHeight = false, dragHandle = null, isDragging = false, highlighted = false,
}: NoteCardProps) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkState, setLinkState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [live, setLive] = useState<number | null>(null); // height while dragging the edge
  // Grid only: a click lifts the card to its full height over its neighbours,
  // keeping its width; its grid cell is held at the collapsed height meanwhile.
  const [expanded, setExpanded] = useState(false);
  const [holdHeight, setHoldHeight] = useState(0);
  const [clipped, setClipped] = useState(false);
  const [editMinHeight, setEditMinHeight] = useState(0); // keep the card's height while editing
  const cardRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; startH: number } | null>(null);
  const keyHeight = useRef<number | null>(null); // last keyboard height, until the saved one catches up
  const isAuthor = currentUserId !== null && note.authorId === currentUserId;
  const name = note.authorDisplayName ?? 'Unknown author';
  const attachedPath = targetPath(note);
  const saved = ignoreSavedHeight ? null : (note.heightPx ?? null);
  const height = live ?? saved;
  const canResize = Boolean(onResize) && isAuthor && canEdit && !ignoreSavedHeight;

  useEffect(() => { keyHeight.current = null; }, [note.heightPx]);

  // Leaving Grid (or editing) always collapses a lifted card.
  useEffect(() => { if (!ignoreSavedHeight || editing) setExpanded(false); }, [ignoreSavedHeight, editing]);

  // Grid: is the text cut off? Drives the fade at the bottom (there is no scrollbar).
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || !ignoreSavedHeight || expanded) { setClipped(false); return; }
    const measure = () => setClipped(body.scrollHeight > body.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, [ignoreSavedHeight, expanded, note.body, editing]);

  function toggleExpanded() {
    if (!expanded) setHoldHeight(Math.round(cardRef.current?.getBoundingClientRect().height ?? 0));
    setExpanded((v) => !v);
  }
  function onCardClick(e: RMouseEvent<HTMLElement>) {
    if (!ignoreSavedHeight) return;
    // Buttons, links and the resize edge keep their own meaning; selecting text is not a click.
    if ((e.target as HTMLElement).closest('button, a, input, textarea, [role="separator"]')) return;
    if (window.getSelection()?.toString()) return;
    toggleExpanded();
  }
  function onCardKey(e: KeyboardEvent<HTMLElement>) {
    if (!ignoreSavedHeight) return;
    if (e.key === 'Escape' && expanded) { e.preventDefault(); setExpanded(false); return; }
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleExpanded(); }
  }

  useEffect(() => {
    if (linkState === 'idle') return;
    const t = window.setTimeout(() => setLinkState('idle'), linkState === 'failed' ? 8000 : 1500);
    return () => window.clearTimeout(t);
  }, [linkState]);

  async function copySource() {
    if (await copyText(note.body)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  }

  async function copyLink() {
    setLinkState((await copyText(notePermalink(note.id))) ? 'copied' : 'failed');
  }

  function onEdgeDown(e: RPointerEvent<HTMLDivElement>) {
    const el = cardRef.current;
    if (!el) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startY: e.clientY, startH: el.getBoundingClientRect().height };
    setLive(clampHeight(drag.current.startH));
  }
  function onEdgeMove(e: RPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    setLive(clampHeight(drag.current.startH + e.clientY - drag.current.startY));
  }
  function onEdgeUp(e: RPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const h = clampHeight(drag.current.startH + e.clientY - drag.current.startY);
    drag.current = null;
    setLive(null);
    if (h !== saved) onResize?.(note.id, h);
  }
  function onEdgeKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const base = keyHeight.current ?? saved ?? Math.round(cardRef.current?.getBoundingClientRect().height ?? 160);
    const next = clampHeight(base + (e.key === 'ArrowDown' ? KEY_STEP : -KEY_STEP));
    keyHeight.current = next;
    onResize?.(note.id, next);
  }

  function startEditing() {
    // The editor opens at the card's current (collapsed) height, never shorter.
    setEditMinHeight(expanded ? holdHeight : Math.round(cardRef.current?.getBoundingClientRect().height ?? 0));
    setEditing(true);
  }

  if (editing) {
    return (
      <NoteEditor
        initialBody={note.body}
        initialColor={note.color}
        minHeight={editMinHeight}
        onCancel={() => setEditing(false)}
        onSave={(draft) => {
          setEditing(false);
          onUpdate(note.id, draft);
        }}
      />
    );
  }

  const cls = [
    'note-card', `note-card--${note.color}`,
    height !== null ? 'note-card--sized' : '',
    highlighted ? 'note-card--highlight' : '',
    isDragging ? 'note-card--dragging' : '',
    expanded ? 'note-card--expanded' : '',
    clipped ? 'note-card--clipped' : '',
  ].filter(Boolean).join(' ');

  const article = (
    <article
      ref={cardRef}
      id={`note-${note.id}`}
      className={cls}
      style={height !== null ? { height } : undefined}
      data-testid="note-card"
      data-note-id={note.id}
      aria-label="Note"
      aria-expanded={ignoreSavedHeight ? expanded : undefined}
      tabIndex={ignoreSavedHeight ? 0 : undefined}
      onClick={onCardClick}
      onKeyDown={onCardKey}
    >
      <div className="note-card__top">
        {dragHandle && (
          <button type="button" className="note-card__icon note-card__grip" aria-label="Drag to reorder" title="Drag to reorder" {...dragHandle}>
            <GripVertical size={14} />
          </button>
        )}
        <span className="note-card__spacer" />
        <button type="button" className="note-card__icon" aria-label="Copy link to note" title={linkState === 'copied' ? 'Link copied' : 'Copy link'} onClick={copyLink}>
          {linkState === 'copied' ? <Check size={14} /> : <Link2 size={14} />}
        </button>
      </div>
      {linkState === 'failed' && (
        <input
          className="note-card__linkfallback"
          readOnly
          aria-label="Note link"
          value={notePermalink(note.id)}
          onFocus={(e) => e.currentTarget.select()}
          ref={(el) => el?.select()}
        />
      )}
      <div ref={bodyRef} className="note-card__body">
        <NoteMarkdown source={note.body} />
      </div>
      {note.targetType && note.targetTitle && attachedPath && (
        <div className="note-card__attached">
          Attached to:{' '}
          <a
            href={attachedPath}
            onClick={(e) => {
              if (onNavigate) {
                e.preventDefault();
                onNavigate(attachedPath);
              }
            }}
          >
            {note.targetTitle}
          </a>
        </div>
      )}
      <div className="note-card__footer">
        <span className="note-card__avatar" title={name} aria-label={`Author: ${name}`}>
          {authorInitials(note.authorDisplayName)}
        </span>
        <time className="note-card__time" dateTime={note.updatedAt} title={new Date(note.updatedAt).toLocaleString()}>
          {relativeTime(note.updatedAt)}
        </time>
        <span className="note-card__spacer" />
        {canResize && (note.heightPx ?? null) !== null && (
          <button type="button" className="note-card__fit" onClick={() => onResize?.(note.id, null)} title="Fit to content">
            <Maximize2 size={12} /> Fit to content
          </button>
        )}
        <button type="button" className="note-card__icon" aria-label="Copy note text" title={copied ? 'Copied' : 'Copy text'} onClick={copySource}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
        {isAuthor && canEdit && (
          <button type="button" className="note-card__icon" aria-label="Edit note" title="Edit" onClick={startEditing}>
            <Pencil size={14} />
          </button>
        )}
        {isAuthor && canDelete && (
          <button type="button" className="note-card__icon" aria-label="Delete note" title="Delete" onClick={() => setConfirming(true)}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {canResize && (
        <div
          className="note-card__edge"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize note height"
          aria-valuemin={80}
          aria-valuemax={1200}
          aria-valuenow={height ?? undefined}
          tabIndex={0}
          onPointerDown={onEdgeDown}
          onPointerMove={onEdgeMove}
          onPointerUp={onEdgeUp}
          onPointerCancel={() => { drag.current = null; setLive(null); }}
          onKeyDown={onEdgeKey}
        />
      )}
      {confirming && (
        <ConfirmModal
          open
          title="Delete this note?"
          message="The note will be removed from this list."
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            onDelete(note.id);
          }}
        />
      )}
    </article>
  );

  // Grid: the slot keeps the cell at its collapsed height while the card is lifted.
  return ignoreSavedHeight ? (
    <div className="note-slot" style={expanded ? { minHeight: holdHeight } : undefined}>{article}</div>
  ) : article;
}
